import debounce from 'lodash/debounce';
import {useEffect, useRef, useState} from 'react';
import {initAligningGuidelines} from '~/common/canvas/controls/align_lines';
import fabric from '~/common/canvas/fabric';

const ENVELOPE_IMAGE_DIMENSIONS = {
  width: 531,
  height: 390,
};
const HEIGHT_TO_WIDTH_ENVELOPE_IMAGE_RATIO =
  ENVELOPE_IMAGE_DIMENSIONS.height / ENVELOPE_IMAGE_DIMENSIONS.width;

interface CanvasJSON {
  version?: string;
  objects: fabric.Object[];
}

interface Options {
  configJson?: CanvasJSON;
  onChange: (json: CanvasJSON) => void;
  setCanvas: (c: fabric.Canvas) => void;
  onLoad?: () => void;
}

export const useStickerCanvas = ({configJson, onChange, setCanvas, onLoad}: Options) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loading, setLoading] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (!canvasRef.current) {
      // biome-ignore lint/suspicious/noEmptyBlockStatements: empty block
      return () => {};
    }

    const canvasInstance = new fabric.Canvas(canvasRef.current, {
      width: ENVELOPE_IMAGE_DIMENSIONS.width,
      height: ENVELOPE_IMAGE_DIMENSIONS.height,
      includeDefaultValues: false,
      selection: !evite.detects.isMobile,
      allowTouchScrolling: true,
    });

    canvasInstance.setZoom(0.5);
    initAligningGuidelines(canvasInstance);

    const canvasSizer =
      document.getElementById('canvas__wrapper__stickers') ||
      document.querySelector('.envelope_image__wrapper__canvas');

    const setCurrentDimensions = () => {
      if (!canvasInstance || !canvasSizer) return;

      const containerWidth = canvasSizer.offsetWidth;
      const containerHeight =
        canvasSizer.offsetHeight || containerWidth * HEIGHT_TO_WIDTH_ENVELOPE_IMAGE_RATIO;

      if (containerWidth > 0 && containerHeight > 0) {
        const scaleX = containerWidth / ENVELOPE_IMAGE_DIMENSIONS.width;
        const scaleY = containerHeight / ENVELOPE_IMAGE_DIMENSIONS.height;
        const scale = Math.min(scaleX, scaleY);

        const newWidth = ENVELOPE_IMAGE_DIMENSIONS.width * scale;
        const newHeight = ENVELOPE_IMAGE_DIMENSIONS.height * scale;

        canvasInstance.setDimensions({width: newWidth, height: newHeight});
        canvasInstance.setZoom(scale * 0.5);
      }
    };

    // @ts-expect-error Fabric wrapper has no TS types
    canvasInstance.setCurrentDimensions = setCurrentDimensions;

    const debouncedSetCurrentDimensions = debounce(() => {
      requestAnimationFrame(setCurrentDimensions);
    }, 200);
    const resizeObserver = new ResizeObserver(debouncedSetCurrentDimensions);
    resizeObserver.observe(canvasSizer!);

    window.addEventListener('resize', setCurrentDimensions, false);

    const onChangeHandler = () => {
      if (!loading) {
        onChange(canvasInstance.toJSON(['dataUrl', 'delay', 'frameWidth', 'framesLength', 'id']));
      }
    };

    // @ts-expect-error Fabric wrapper has no TS types
    canvasInstance.on({
      'object:modified': onChangeHandler,
      'object:added': onChangeHandler,
      'object:removed': onChangeHandler,
    });

    const loadCanvas = (config: CanvasJSON) => {
      setLoading(true);
      const reviver = async (json: CanvasJSON, obj: fabric.Object) => {
        setLoading(false);
        if (onLoad) onLoad();
        setCurrentDimensions();
        obj.set({selectable: true});
        obj.setControlsVisibility({
          mtr: true,
          // @ts-expect-error Fabric wrapper has no TS types
          remove: true,
        });
      };
      // biome-ignore lint/suspicious/noEmptyBlockStatements: empty block
      canvasInstance.loadFromJSON(config, () => {}, reviver);
    };
    if (configJson) loadCanvas(configJson);

    setCurrentDimensions();

    setCanvas(canvasInstance);
    return () => {
      canvasInstance.dispose();
      resizeObserver.disconnect();
      window.removeEventListener('resize', setCurrentDimensions);
    };
  }, []);

  return {canvasRef, loading};
};
